import {ChangeDetectionStrategy, Component, computed, inject} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {catchError, map, Observable, of, startWith, switchMap} from 'rxjs';
import {DatePipe, LoadComponent} from '@brunata-angular/ui';
import {KeyValuesRowComponent} from '@shared/ui/key-values-row/key-values-row.component';
import {formatStreetAddress, TranslatePipe} from '@brunata-angular/util';
import {InstallationProjectsService, isNotFound} from '../installation-projects.service';
import {
    INSTALLATION_PROJECT_STATUSES,
    formatVisitTimeSlot,
    InstallationProjectDetail,
    installationProjectStatusDisplay,
    InstallationProjectStatus,
} from '../installation-projects.model';

type OverviewView =
    | {status: 'loading'}
    | {status: 'notFound'}
    | {status: 'error'}
    | {status: 'success'; project: InstallationProjectDetail};

type ProgressStep = {
    labelKey: string;
    state: 'completed' | 'active' | 'future';
};

type SummaryCounter = {
    labelKey: string;
    value: number;
    textClass: string;
    route: string | null;
};

/** Relative to the overview tab, so it stays correct wherever the detail route is mounted. */
const LOCATIONS_TAB_ROUTE = '../locations';

@Component({
    selector: 'app-installation-project-overview',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslatePipe, LoadComponent, DatePipe, KeyValuesRowComponent, RouterLink],
    templateUrl: './installation-project-overview.component.html',
    styleUrl: './installation-project-overview.component.scss',
})
export class InstallationProjectOverviewComponent {
    private readonly installationProjectsService = inject(InstallationProjectsService);
    private readonly activatedRoute = inject(ActivatedRoute);

    protected readonly view = toSignal(
        // The project id lives on the parent `:projectId` segment; a tab child with its own path
        // does not inherit it (the router's default emptyOnly strategy).
        (this.activatedRoute.parent ?? this.activatedRoute).paramMap.pipe(
            map(params => params.get('projectId')),
            switchMap(projectId =>
                projectId == null
                    ? of<OverviewView>({status: 'notFound'})
                    : this.loadProject(projectId),
            ),
        ),
        {initialValue: {status: 'loading'}},
    );

    protected readonly project = computed(() => {
        const view = this.view();
        return view.status === 'success' ? view.project : null;
    });

    protected readonly address = computed(() => {
        const project = this.project();
        if (project == null) {
            return '';
        }
        return formatStreetAddress({
            address1: project.street,
            address2: null,
            postalCode: project.postalCode,
            city: project.city,
        });
    });

    protected readonly statusDisplay = computed(() => {
        const project = this.project();
        return project == null ? null : installationProjectStatusDisplay(project.status);
    });

    /** The 7-phase lifecycle with each phase placed relative to the project's current status. */
    protected readonly progressSteps = computed<ProgressStep[]>(() => {
        const project = this.project();
        if (project == null) {
            return [];
        }
        const currentIndex = lifecycleIndex(project.status);
        return INSTALLATION_PROJECT_STATUSES.map((status, index) => ({
            labelKey: installationProjectStatusDisplay(status).labelKey,
            state: stepState(index, currentIndex),
        }));
    });

    protected readonly visitWindow = computed(() => {
        const nextVisit = this.project()?.nextVisit;
        if (nextVisit == null) {
            return null;
        }
        return formatVisitTimeSlot(nextVisit.startTime, nextVisit.endTime);
    });

    /**
     * The fill ends at the centre of the active label - the labels sit in equal-width columns,
     * so the centre of column i is at (i + 0.5) sevenths. A completed project fills the bar.
     */
    protected readonly progressPercent = computed(() => {
        const project = this.project();
        if (project == null) {
            return 0;
        }
        if (project.status === 'completed') {
            return 100;
        }
        const currentIndex = lifecycleIndex(project.status);
        if (currentIndex === -1) {
            return 0;
        }
        return ((currentIndex + 0.5) / INSTALLATION_PROJECT_STATUSES.length) * 100;
    });

    /** Stringified for the key/value row, which takes display strings. */
    protected readonly locationTotal = computed(
        () => this.project()?.locations.total.toString() ?? '',
    );

    protected readonly locationsTabRoute = LOCATIONS_TAB_ROUTE;

    /** The four summary cards, in the mock's order. Only the total opens the locations tab. */
    protected readonly counters = computed<SummaryCounter[]>(() => {
        const locations = this.project()?.locations;
        if (locations == null) {
            return [];
        }
        return [
            {
                labelKey: 'installationProjects_detail_locations',
                value: locations.total,
                textClass: 'text-primary',
                route: LOCATIONS_TAB_ROUTE,
            },
            {
                labelKey: 'installationProjects_detail_inProgress',
                value: locations.inProgress,
                textClass: 'text-primary',
                route: null,
            },
            {
                labelKey: 'installationProjects_detail_visitsCompleted',
                value: locations.completed,
                textClass: 'text-success',
                route: null,
            },
            {
                labelKey: 'installationProjects_detail_outstanding',
                value: locations.outstanding,
                textClass: 'text-warning',
                route: null,
            },
        ];
    });

    /** Without a next visit the banner explains why, derived from the project status (story §4). */
    protected readonly noVisitMessageKey = computed(() => {
        switch (this.project()?.status) {
            case 'awaiting_customer_info':
                return 'installationProjects_status_awaitingCustomerInfo';
            case 'completed':
                return 'installationProjects_detail_projectCompleted';
            default:
                return 'installationProjects_detail_noNextVisit';
        }
    });

    private loadProject(projectId: string): Observable<OverviewView> {
        return this.installationProjectsService.getInstallationProject(projectId).pipe(
            map((project): OverviewView => ({status: 'success', project})),
            startWith<OverviewView>({status: 'loading'}),
            catchError(failure =>
                of<OverviewView>(isNotFound(failure) ? {status: 'notFound'} : {status: 'error'}),
            ),
        );
    }
}

/**
 * A status Brunata Online does not know has no place on the stepper, so no phase is marked active
 * rather than one being guessed.
 */
function lifecycleIndex(status: InstallationProjectStatus): number {
    // Widened so indexOf accepts `unknown`, which the lifecycle tuple deliberately excludes.
    const lifecycle: readonly InstallationProjectStatus[] = INSTALLATION_PROJECT_STATUSES;
    return lifecycle.indexOf(status);
}

function stepState(index: number, currentIndex: number): ProgressStep['state'] {
    if (currentIndex === -1 || index > currentIndex) {
        return 'future';
    }
    return index === currentIndex ? 'active' : 'completed';
}
